// Tournament API client. Thin layer over the chalkApi client — follows
// the envelope-unwrap convention.
import { client, unwrap } from "./chalkApi";

const T = (id) => `/tournaments/${id}`;

export const tournaments = {
  list: () => unwrap(client.get("/tournaments")),
  create: (body) => unwrap(client.post("/tournaments", body)),
  get: (id) => unwrap(client.get(T(id))),
  patch: (id, body) => unwrap(client.patch(T(id), body)),
  delete: (id) => unwrap(client.delete(T(id))),
  inviteHelper: (id, email) => unwrap(client.post(`${T(id)}/helpers`, { email })),
  revokeHelper: (id, hid) => unwrap(client.delete(`${T(id)}/helpers/${hid}`)),
  addTeam: (id, body) => unwrap(client.post(`${T(id)}/teams`, body)),
  bulkAddTeams: (id, names, divisionId) =>
    unwrap(client.post(`${T(id)}/teams/bulk`, { names, division_id: divisionId })),
  patchTeam: (id, tid, body) => unwrap(client.patch(`${T(id)}/teams/${tid}`, body)),
  deleteTeam: (id, tid) => unwrap(client.delete(`${T(id)}/teams/${tid}`)),
  buildBracket: (id, body) => unwrap(client.post(`${T(id)}/bracket/build`, body)),
  resetBracket: (id) => unwrap(client.post(`${T(id)}/bracket/reset`)),
  patchGame: (id, gid, body) => unwrap(client.patch(`${T(id)}/games/${gid}`, body)),
  gameHistory: (id, gid) => unwrap(client.get(`${T(id)}/games/${gid}/history`)),
  // Hub (Phase 2)
  createDivision: (id, name) => unwrap(client.post(`${T(id)}/divisions`, { name })),
  patchDivision: (id, did, body) => unwrap(client.patch(`${T(id)}/divisions/${did}`, body)),
  reorderDivisions: (id, order) => unwrap(client.post(`${T(id)}/divisions/reorder`, { order })),
  deleteDivision: (id, did) => unwrap(client.delete(`${T(id)}/divisions/${did}`)),
  buildDivision: (id, did, body) => unwrap(client.post(`${T(id)}/divisions/${did}/bracket/build`, body)),
  resetDivision: (id, did) => unwrap(client.post(`${T(id)}/divisions/${did}/bracket/reset`)),
  createCourt: (id, name) => unwrap(client.post(`${T(id)}/courts`, { name })),
  patchCourt: (id, cid, body) => unwrap(client.patch(`${T(id)}/courts/${cid}`, body)),
  deleteCourt: (id, cid) => unwrap(client.delete(`${T(id)}/courts/${cid}`)),
  scheduleGame: (id, gid, body) => unwrap(client.patch(`${T(id)}/games/${gid}/schedule`, body)),
  autofill: (id, body) => unwrap(client.post(`${T(id)}/schedule/autofill`, body)),
  addPlayer: (id, tid, body) => unwrap(client.post(`${T(id)}/teams/${tid}/players`, body)),
  removePlayer: (id, tid, pid) => unwrap(client.delete(`${T(id)}/teams/${tid}/players/${pid}`)),
  rosterConflicts: (id) => unwrap(client.get(`${T(id)}/roster-conflicts`)),
  checkin: (id, tid, status) => unwrap(client.post(`${T(id)}/teams/${tid}/checkin`, { status })),
  forfeitNext: (id, tid) => unwrap(client.post(`${T(id)}/teams/${tid}/forfeit-next`)),
  checkinLink: (id, tid, body) => unwrap(client.post(`${T(id)}/teams/${tid}/checkin-link`, body)),
  revokeCheckinLink: (id, tid) => unwrap(client.delete(`${T(id)}/teams/${tid}/checkin-link`)),
  createAnnouncement: (id, body) => unwrap(client.post(`${T(id)}/announcements`, body)),
  patchAnnouncement: (id, aid, body) => unwrap(client.patch(`${T(id)}/announcements/${aid}`, body)),
  deleteAnnouncement: (id, aid) => unwrap(client.delete(`${T(id)}/announcements/${aid}`)),
  setFormat: (id, did, body) => unwrap(client.patch(`${T(id)}/divisions/${did}/format`, body)),
  generatePools: (id, did, n) => unwrap(client.post(`${T(id)}/divisions/${did}/pools/generate`, { pool_count: n })),
  movePoolTeam: (id, did, body) => unwrap(client.post(`${T(id)}/divisions/${did}/pools/move`, body)),
  lockPools: (id, did) => unwrap(client.post(`${T(id)}/divisions/${did}/pools/lock`)),
  standings: (id, did) => unwrap(client.get(`${T(id)}/divisions/${did}/standings`)),
  tiebreak: (id, did, body) => unwrap(client.post(`${T(id)}/divisions/${did}/tiebreak`, body)),
  seedPreview: (id, did) => unwrap(client.get(`${T(id)}/divisions/${did}/seed-preview`)),
  advancePools: (id, did, body) => unwrap(client.post(`${T(id)}/divisions/${did}/pools/advance`, body || {})),
  previewUpgrade: (id) => unwrap(client.post(`${T(id)}/preview-upgrade`)),
  adminTier: (id, body) => unwrap(client.post(`${T(id)}/admin/tier`, body)),
  // Showcase (Phase 4)
  branding: (id) => unwrap(client.get(`${T(id)}/branding`)),
  uploadLogo: (id, file) => unwrap(client.post(`${T(id)}/logo`, form({ file }))),
  removeLogo: (id) => unwrap(client.delete(`${T(id)}/logo`)),
  addSponsor: (id, { name, url, file }) => unwrap(client.post(`${T(id)}/sponsors`, form({ name, url, file }))),
  patchSponsor: (id, sid, body) => unwrap(client.patch(`${T(id)}/sponsors/${sid}`, body)),
  sponsorLogo: (id, sid, file) => unwrap(client.post(`${T(id)}/sponsors/${sid}/logo`, form({ file }))),
  deleteSponsor: (id, sid) => unwrap(client.delete(`${T(id)}/sponsors/${sid}`)),
  reorderSponsors: (id, order) => unwrap(client.post(`${T(id)}/sponsors/reorder`, { order })),
  setScreen: (id, body) => unwrap(client.patch(`${T(id)}/screen`, body)),
  newScreenCode: (id) => unwrap(client.post(`${T(id)}/screen/code`)),
  claims: (id) => unwrap(client.get(`${T(id)}/claims`)),
  sendClaims: (id, teamIds) => unwrap(client.post(`${T(id)}/claims/send`, { team_ids: teamIds || null })),
};

function form(fields) {
  const f = new FormData();
  Object.entries(fields).forEach(([k, v]) => { if (v !== undefined && v !== null && v !== "") f.append(k, v); });
  return f;
}

export const tbilling = {
  terms: () => unwrap(client.get("/tournament-billing/terms")),
  event: (id) => unwrap(client.get(`/tournament-billing/tournaments/${id}`)),
  upgrade: (id, body) => unwrap(client.post(`/tournament-billing/tournaments/${id}/upgrade`, body)),
  claimGrant: (id) => unwrap(client.post(`/tournament-billing/tournaments/${id}/claim-grant`)),
  director: () => unwrap(client.get("/tournament-billing/director")),
  directorCheckout: (body) => unwrap(client.post("/tournament-billing/director/checkout", body)),
  directorChange: (body) => unwrap(client.post("/tournament-billing/director/change", body)),
  balanceCheckout: (body) => unwrap(client.post("/tournament-billing/balance/checkout", body)),
  checkout: (sid) => unwrap(client.get(`/tournament-billing/checkouts/${sid}`)),
  previewComplete: (body) => unwrap(client.post("/tournament-billing/preview/complete", body)),
  adminGrant: (body) => unwrap(client.post("/tournament-billing/admin/free-grant", body)),
};

export const director = {
  savedTeams: () => unwrap(client.get("/director/saved-teams")),
  importTeams: (id, names) => unwrap(client.post(`/director/tournaments/${id}/import-teams`, { names })),
  copy: (id, body) => unwrap(client.post(`/director/tournaments/${id}/copy`, body)),
  setSlug: (id, slug) => unwrap(client.patch(`/director/tournaments/${id}/slug`, { slug })),
  handle: () => unwrap(client.get("/director/handle")),
  setHandle: (slug) => unwrap(client.put("/director/handle", { slug })),
  signups: () => unwrap(client.get("/director/signups")),
  notify: (tournamentId) => unwrap(client.post("/director/signups/notify", { tournament_id: tournamentId })),
};

export const publicSignup = (slug, body) => unwrap(client.post(`/public/tournaments/${slug}/signup`, body));
export const publicArchive = (handle) => unwrap(client.get(`/public/directors/${handle}`));
export const usd = (c) => `$${(Math.abs(c || 0) / 100).toLocaleString(undefined, { maximumFractionDigits: (c || 0) % 100 ? 2 : 0 })}`;

export const resolveScreenCode = (code) => unwrap(client.get(`/screens/${encodeURIComponent(code)}`));

export const claims = {
  view: (token) => unwrap(client.get(`/claims/${token}`)),
  accept: (token) => unwrap(client.post(`/claims/${token}/accept`)),
};

const PUBLIC = () => `${process.env.REACT_APP_BACKEND_URL}/api/v1/public/tournaments`;

// Anonymous per-browser id so the server can rate-limit per device rather than
// per IP (a whole gym shares one Wi-Fi IP).
export function deviceId() {
  const KEY = "chalkos.device";
  try {
    let id = localStorage.getItem(KEY);
    if (!id) {
      id = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`).replace(/[^A-Za-z0-9_-]/g, "");
      localStorage.setItem(KEY, id);
    }
    return id;
  } catch { return ""; }
}

async function publicCall(path, { method = "GET", body } = {}) {
  const r = await fetch(`${PUBLIC()}${path}`, {
    method,
    headers: {
      Accept: "application/json", "X-Device-Id": deviceId(),
      ...(body ? { "Content-Type": "application/json" } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j.success === false) {
    const err = new Error(j.error?.message || `HTTP ${r.status}`);
    err.code = j.error?.code;
    err.status = r.status;
    throw err;
  }
  return j.data;
}

// Client-managed ETag: some proxies force no-store, so the browser cache can't revalidate.
const _etags = {};
export async function fetchPublicTournament(slug, prev) {
  const r = await fetch(`${PUBLIC()}/${slug}`, {
    headers: { Accept: "application/json", "X-Device-Id": deviceId(),
               ...(prev && _etags[slug] ? { "If-None-Match": _etags[slug] } : {}) },
  });
  if (r.status === 304 && prev) return prev;
  const j = await r.json().catch(() => ({}));
  if (!r.ok || j.success === false) {
    const err = new Error(j.error?.message || `HTTP ${r.status}`);
    err.code = j.error?.code;
    err.status = r.status;
    throw err;
  }
  _etags[slug] = r.headers.get("ETag");
  return j.data;
}
export const assetUrl = (path) => (path ? `${process.env.REACT_APP_BACKEND_URL}${path}` : null);
export const resultsPngUrl = (slug, did, size = "portrait", download = false) =>
  `${PUBLIC()}/${slug}/results/${did}.png?size=${size}${download ? "&download=1" : ""}`;
export const resultsShareUrl = (slug, did) => `${PUBLIC()}/${slug}/share?division=${did}`;
export const publicManifestUrl = (slug) => `${PUBLIC()}/${slug}/manifest.webmanifest`;
export const captainCheckinView = (slug, token) => publicCall(`/${slug}/checkin/${token}`);
export const captainCheckin = (slug, token) => publicCall(`/${slug}/checkin/${token}`, { method: "POST" });
export const publicFollow = (slug, body) => publicCall(`/${slug}/follow`, { method: "POST", body });
export const publicUnfollow = (slug, body) => publicCall(`/${slug}/follow`, { method: "DELETE", body });
